import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, Share2Icon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CAMPAIGN_CHANNEL_IMAGE = 'https://cdn.magicpatterns.com/uploads/drdztpnvaoUPvSdPcUuzwR/Screenshot_2026-08-27_at_10.57.18.png';
const CHANNELS = [{
  label: 'Embed',
  detail: 'Websites and portals',
  logo: 'https://cdn.magicpatterns.com/uploads/p8hxGk8PzPNEJJ1JuXxQRJ/image.png'
}, {
  label: 'Text',
  detail: 'SMS and direct links',
  logo: 'https://cdn.magicpatterns.com/uploads/2mqR9WX6acGmcEwvCVyefL/image.png'
}, {
  label: 'WhatsApp',
  detail: 'Conversation-ready',
  logo: 'https://cdn.magicpatterns.com/uploads/ff2xrTo8njegc9HQfBiHHs/image.jpg'
}, {
  label: 'QR Code',
  detail: 'Print and events',
  logo: 'https://cdn.magicpatterns.com/uploads/5EBPJkAW8wtsVjG6Pdmtzv/image.png'
}, {
  label: 'Post',
  detail: 'Organic and paid social',
  logo: 'https://cdn.magicpatterns.com/uploads/i3HXhvnPCcq7MnMVtgzoGz/image.png'
}, {
  label: 'Email',
  detail: 'CRM and newsletters',
  logo: 'https://cdn.magicpatterns.com/uploads/eaVbSy8gkaLMxVXZn426xD/image.png'
}, {
  label: 'Advertise',
  detail: 'Banners and paid media',
  logo: 'https://cdn.magicpatterns.com/uploads/x8cuoAZY8NxEd27vHPMXd5/image.png'
}, {
  label: 'Congress',
  detail: 'Booths and sessions',
  logo: 'https://cdn.magicpatterns.com/uploads/gFpiinUzyP3uSTeNYjp6JX/image.png'
}] as const;
export function Slide03Background({}: SlideProps) {
  return <InteriorSlide surface data-id="element-103" data-mp="3roqv7hpkilj1">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.25rem,2vw,2rem)] flex-shrink-0" data-id="element-104" data-mp="3rnyxo1ceudq4">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-105" data-mp="o2t9xxk48itg">
          Channel-agnostic distribution
        </p>
        <h2 className="max-w-5xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-106" data-mp="2dim19ebt0k23">
          One experience.
          <span className="text-[var(--brand)]" data-id="element-107" data-mp="23su5whvvrmwk"> More channels to share.</span>
        </h2>
        <p className="mt-3 max-w-3xl text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-108" data-mp="28r3xyjmb67dy">
          A single Snak can meet HCPs wherever attention already exists—without
          rebuilding the experience for every channel.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-7 lg:grid-cols-12 lg:gap-10" data-id="element-109" data-mp="1zocp8rkthpq0">
        <motion.article initial={{
        opacity: 0,
        x: -24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.18,
        duration: 0.6
      }} className="relative flex min-h-0 flex-col overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.25rem,2vw,2rem)] text-white shadow-lg lg:col-span-5" data-id="element-110" data-mp="1kvj3ioeycdcw">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-111" data-mp="18invi6x4abb1" />
          <div className="relative flex items-center gap-3" data-id="element-112" data-mp="13kvsagr0v2rj">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-113" data-mp="13i9kcg07yl8s">
              <MessageCircleIcon className="h-6 w-6" aria-hidden="true" data-id="element-114" data-mp="1iatn3fjgiqpp" />
            </span>
            <div data-id="element-115" data-mp="1dggykr34ip73">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-white/65" data-id="element-116" data-mp="1r9ez0rufb7hi">
                Example entry point
              </p>
              <h3 className="text-2xl font-extrabold" data-id="element-117" data-mp="1n7bms0zxjyv6">WhatsApp</h3>
            </div>
          </div>

          <div className="relative mx-auto mt-5 min-h-0 w-full max-w-sm flex-1 overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-white/25" data-id="element-118" data-mp="216jq9634lt56">
            <img src={CAMPAIGN_CHANNEL_IMAGE} alt="Healthcare campaign shared through WhatsApp" className="h-full w-full object-cover object-[31%_center]" data-id="element-119" data-mp="6tlgcnlc8yid" />
          </div>
        </motion.article>

        <motion.section initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-7" aria-labelledby="channel-heading" data-id="element-120" data-mp="3be7deihjhwv">
          <div className="flex items-end justify-between gap-4" data-id="element-121" data-mp="i9gefyoq6liv">
            <div data-id="element-122" data-mp="3gqu3g9g4alpw">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-123" data-mp="3grc5jmpgnu5q">
                The same Snak
              </p>
              <h3 id="channel-heading" className="mt-1 text-2xl font-extrabold text-[var(--fg)]" data-id="element-124" data-mp="3lqf3yoaxr24q">
                Ready for every relevant touchpoint
              </h3>
            </div>
            <span className="hidden rounded-full bg-[var(--brand)]/10 px-4 py-2 text-xs font-bold text-[var(--brand)] sm:inline-flex" data-id="element-125" data-mp="3vgi3768nhcbi">
              One build · many entries
            </span>
          </div>

          <div className="mt-5 grid min-h-0 flex-1 grid-cols-2 gap-3 md:grid-cols-4" data-id="element-126" data-mp="32xuoc8k8ytiv">
            {CHANNELS.map((channel, index) => <motion.article key={channel.label} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.42 + index * 0.06,
            duration: 0.4
          }} className="group flex min-h-0 flex-col justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm transition-transform hover:-translate-y-1" data-id="element-127" data-mp="1g2pq8oituaha" data-mpi={index}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface)] p-1.5" data-id="element-128" data-mp="we70a4pzyt1j">
                  <img src={channel.logo} alt={`${channel.label} channel logo`} className="h-full w-full object-contain" data-id="element-129" data-mp="se6iqb7lq1q9" />
                </span>
                <div className="mt-4" data-id="element-130" data-mp="ie5wagefr5hu">
                  <h4 className="text-base font-extrabold text-[var(--fg)]" data-id="element-131" data-mp="dg4dlwkx986w">{channel.label}</h4>
                  <p className="mt-1 text-[10px] leading-snug text-[var(--muted)]" data-id="element-132" data-mp="3s8m57n6ttta">{channel.detail}</p>
                </div>
              </motion.article>)}
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-5 py-3" data-id="element-133" data-mp="8oyz3m3p49ah">
            <Share2Icon className="h-5 w-5 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-134" data-mp="3uy07ltq0si9r" />
            <p className="text-sm font-semibold text-[var(--fg)]" data-id="element-135" data-mp="mi865g468ipu">
              Consistent experience, consistent measurement—regardless of where the journey begins.
            </p>
          </div>
        </motion.section>
      </div>
    </InteriorSlide>;
}